import { type MutableRefObject, useEffect } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

/**
 * Selection resets on entering a tool mode whose own UI owns the canvas: shape creation, the pen
 * tool (which auto-enters node edit for a lone selected path), pen edit, and node edit.
 */
export function useToolModeSelectionReset({
  internalShapeManagerRef,
  isInShapeCreationMode,
  isInPenToolMode,
  isPenEditMode,
  isNodeEditMode,
  nodeEditShapeIdRef,
  clearShapeSelectionWithHistory,
  tryEnterNodeEditMode,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  isInShapeCreationMode: boolean
  isInPenToolMode: boolean
  isPenEditMode: boolean
  isNodeEditMode: boolean
  nodeEditShapeIdRef: MutableRefObject<string | null>
  clearShapeSelectionWithHistory: (afterTextIds?: string[]) => void
  tryEnterNodeEditMode: (shape: ShapeData) => boolean
}) {
  // When entering shape creation mode, ensure no existing objects appear selected.
  useEffect(() => {
    if (!isInShapeCreationMode) return
    clearShapeSelectionWithHistory([])
  }, [clearShapeSelectionWithHistory, isInShapeCreationMode])

  // When entering pen tool mode: if a single pen-drawn path is selected, auto-enter node-edit
  // mode for it so the cursor shows +/- on hover immediately. Otherwise clear selection.
  useEffect(() => {
    if (!isInPenToolMode) return
    const mgr = internalShapeManagerRef.current
    const selectedIds = mgr?.getSelectedShapeIds() ?? []

    if (selectedIds.length === 1 && !nodeEditShapeIdRef.current) {
      const shape = mgr?.getShapes().find((s) => s.id === selectedIds[0])

      if (shape?.type === 'path' && shape.path && tryEnterNodeEditMode(shape)) {
        clearShapeSelectionWithHistory([])
        return
      }
    }

    clearShapeSelectionWithHistory([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearShapeSelectionWithHistory, isInPenToolMode])

  // Pen add/remove sub-mode: hide selection chrome; node overlay owns interaction.
  useEffect(() => {
    if (!isPenEditMode) return
    clearShapeSelectionWithHistory([])
  }, [clearShapeSelectionWithHistory, isPenEditMode])

  // Node edit mode: clear selection so the chrome outline never overlaps bezier handles.
  // Mirrors the isPenEditMode guard above. On exit, Escape/Enter re-selects the shape.
  useEffect(() => {
    if (!isNodeEditMode) return
    internalShapeManagerRef.current?.clearSelection()
  }, [isNodeEditMode, internalShapeManagerRef])
}
